import type { BuildSourceRoster, SourceRosterEntry } from "./types";

/**
 * Every feed point on this plan, struck ones included.
 *
 * A struck board stays in the list: it is the one he most needs to see, both to
 * put it back and to know why six circuits went quiet. Dropping it is how the
 * run's mistake became unrepairable in the first place.
 */
export const buildSourceRoster: BuildSourceRoster = ({
  baseSources,
  addedSources,
  removedSources,
  sourceNames,
  feedingBoardByCircuit,
  lengthsByCircuit,
}) => {
  const carried = new Map<string, { count: number; metres: number; ids: string[] }>();
  Object.entries(feedingBoardByCircuit).forEach(([circuitId, label]) => {
    if (!label) return;
    const held = carried.get(label) ?? { count: 0, metres: 0, ids: [] };
    held.count += 1;
    held.metres += lengthsByCircuit[circuitId] ?? 0;
    held.ids.push(circuitId);
    carried.set(label, held);
  });

  const entries: SourceRosterEntry[] = [];
  const seen = new Set<string>();

  const push = (
    label: string,
    name: string,
    isAdded: boolean,
    isUncertain: boolean,
    system?: SourceRosterEntry["system"],
  ) => {
    if (seen.has(label)) return;
    seen.add(label);
    const held = carried.get(label);
    entries.push({
      label,
      name: sourceNames[label] || name || label,
      isRemoved: Boolean(removedSources[label]),
      isAdded,
      isUncertain,
      system,
      circuitCount: held?.count ?? 0,
      metres: held?.metres ?? 0,
      circuitIds: held?.ids ?? [],
    });
  };

  baseSources.forEach((source) =>
    push(
      source.label,
      source.name,
      false,
      Boolean(source.uncertain),
      source.system,
    ),
  );
  // After the run's own, in the order he added them: his are the exceptions and
  // belong where he last left them, not interleaved by name.
  Object.entries(addedSources).forEach(([label, source]) =>
    push(label, source.name, true, false),
  );

  return entries;
};
